import React from "react";
import "./index.scss";
import Routes from "./containers/routes";
import "core-js/stable";
import "regenerator-runtime/runtime";
import "./i18n";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastRenderer } from "components/Toast";
import { createRoot } from "react-dom/client";
import configureStore from "./store";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "rc-time-picker/assets/index.css";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "services/msalInstance";
import constants from "constants";
import { defaultScopes, buildAcquireTokenSilentRequest } from "services/authProvider";
import { passwordResetAuthority } from "services/authProviderReset";
import { recoverMsalSessionFromCache, applySyntheticAccountFromCache } from "services/msalSessionRecovery";
import { ensureAxiosBearerInterceptor } from "utils/setupAxiosBearer";
import {
  cleanMsalRedirectUrl,
  clearLogoutSessionFlags,
  isReturningFromLogoutResponse,
} from "utils/appLogout";
import {
  isB2cForgotPasswordRedirectError,
  isB2cUserCancelledError,
  scheduleB2cSignInRedirect,
} from "utils/b2cRedirectErrors";

/** Before any React tree runs — APIs under Routes can fire without mfa-validate yet. */
ensureAxiosBearerInterceptor();

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_URL,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

window.onerror = function (message, source, lineno, colno, error) {
  Sentry.captureException(error);
};

window.onunhandledrejection = function (event) {
  Sentry.captureException(event.reason);
};

if (process.env.NODE_ENV !== "production") {
  console.log(process.env, "env");
}

const store = configureStore();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const container = document.getElementById("root");
const root = createRoot(container);

const appTree = (
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <BrowserRouter>
        <MsalProvider instance={msalInstance}>
          <Routes />
          <ReactQueryDevtools initialIsOpen={false} />
          <ToastRenderer />
        </MsalProvider>
      </BrowserRouter>
    </Provider>
  </QueryClientProvider>
);

/**
 * B2C SUSI: "Forgot your password?" does not navigate to the reset policy itself.
 * It redirects back with `access_denied` / AADB2C90118. MSAL rejects
 * `handleRedirectPromise` — we must start the password-reset user journey here.
 * Cancel on password-reset returns AADB2C90091 — redirect straight to SUSI sign-in.
 * @see https://learn.microsoft.com/en-us/azure/active-directory-b2c/add-password-reset-policy?pivots=b2c-user-flow#self-service-password-reset-recommended
 */
/**
 * Process the B2C redirect response before the first React paint so the hash
 * (#state=…&code=…) is consumed, accounts exist in the PCA cache, and
 * MsalProvider can unblock from Startup without leaving the app on the public
 * shell with a stale URL.
 */
function bootstrapMsalThenRender() {
  return msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .then(async (response) => {
      const returningFromLogout = isReturningFromLogoutResponse(response);

      if (isB2cUserCancelledError(null)) {
        scheduleB2cSignInRedirect(msalInstance);
        return false;
      }

      // Persist id token before React mounts so axios / react-query on first paint
      // do not race useAppSharedState (fixes missing Authorization on first load).
      const fromRedirect = response?.idToken;
      if (fromRedirect) {
        localStorage.setItem("msal.idtoken", fromRedirect);
        clearLogoutSessionFlags();
        sessionStorage.removeItem(
          constants.sessionStorageKeys.pendingLoginRedirect,
        );
      }
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      } else if (!msalInstance.getActiveAccount()) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      }

      if (returningFromLogout) {
        clearLogoutSessionFlags();
        cleanMsalRedirectUrl();
        return true;
      }

      // Edge: apply cached session synchronously — never block first paint on ssoSilent.
      applySyntheticAccountFromCache(msalInstance);
      await recoverMsalSessionFromCache(msalInstance);

      if (!localStorage.getItem("msal.idtoken")) {
        const account = msalInstance.getActiveAccount();
        if (account) {
          try {
            const silent = await msalInstance.acquireTokenSilent(
              buildAcquireTokenSilentRequest(account),
            );
            if (silent?.idToken) {
              localStorage.setItem("msal.idtoken", silent.idToken);
            }
          } catch (e) {
            console.warn(
              "Bootstrap: acquireTokenSilent before render failed:",
              e,
            );
          }
        }
      }

      cleanMsalRedirectUrl();
      return true;
    })
    .then((shouldRender) => {
      if (shouldRender === false) {
        return;
      }
      if (!localStorage.getItem("msal.idtoken")) {
        sessionStorage.removeItem(
          constants.sessionStorageKeys.pendingLoginRedirect,
        );
      }
      root.render(appTree);
    })
    .catch((err) => {
      if (isB2cUserCancelledError(err)) {
        scheduleB2cSignInRedirect(msalInstance);
        return;
      }
      if (isB2cForgotPasswordRedirectError(err)) {
        if (passwordResetAuthority) {
          // Defer so MSAL can finish tearing down the failed redirect interaction
          // (avoids interaction_in_progress on some msal-browser versions).
          setTimeout(() => {
            msalInstance.loginRedirect({
              authority: passwordResetAuthority,
              scopes: defaultScopes,
            });
          }, 0);
          return;
        }
        console.error(
          "Forgot-password (AADB2C90118) detected but password reset authority is not configured. Set REACT_APP_AZURE_AD_AUTHORITY_RESET or a SUSI URL containing B2C_1A_TRUSTFRAMEWORKBASE_SUSI for derivation.",
        );
      }
      console.error("MSAL bootstrap failed:", err);
      clearLogoutSessionFlags();
      root.render(appTree);
    });
}

bootstrapMsalThenRender();
